import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import { visitsAPI, redemptionsAPI, lookupAPI } from '@/lib/api';
import { formatPoints, formatDate, formatRelativeTime, getInitials } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  QrCode,
  Gift,
  User,
  LogOut,
  Scan,
  Check,
  X,
  Trophy,
  Clock,
  History,
  Store,
  ChevronRight,
  AlertCircle,
  Loader2
} from 'lucide-react';

// Store Selector for Staff
function StoreAppSelector({ onSelect }) {
  const { stores, loading } = useStore();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8 tablet-optimized">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-indigo-600/20 flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-indigo-400" />
          </div>
          <h1 className="font-outfit text-3xl font-bold text-foreground">Store App</h1>
          <p className="text-muted-foreground mt-2">Selecciona tu tienda para comenzar</p>
        </div>
        
        <div className="space-y-4">
          {stores.map((store) => (
            <Card
              key={store.id}
              className="glass border-white/10 cursor-pointer card-hover"
              onClick={() => onSelect(store)}
              data-testid={`store-select-${store.id}`}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-indigo-600/20 flex items-center justify-center">
                  <Store className="w-7 h-7 text-indigo-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit text-lg font-semibold text-foreground">{store.name}</h3>
                  <p className="text-muted-foreground">{store.description || 'Programa de fidelización'}</p>
                </div>
                <ChevronRight className="w-6 h-6 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// Main Store App Dashboard
function StoreAppDashboard() {
  const { user, logout } = useAuth();
  const { currentStore, clearStore } = useStore();
  const navigate = useNavigate();
  const [todayVisits, setTodayVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualQR, setManualQR] = useState('');
  const [customerData, setCustomerData] = useState(null);
  const [processingVisit, setProcessingVisit] = useState(false);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [processingRedeem, setProcessingRedeem] = useState(false);

  useEffect(() => {
    loadTodayVisits();
    // Refresh every 30 seconds
    const interval = setInterval(loadTodayVisits, 30000);
    return () => clearInterval(interval);
  }, [currentStore]);

  const loadTodayVisits = async () => {
    try {
      const response = await visitsAPI.listToday(currentStore.id);
      setTodayVisits(response.data);
    } catch (err) {
      console.error('Error loading visits:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleScanQR = async (qrToken) => {
    if (!qrToken.trim()) {
      toast.error('Ingresa un código QR válido');
      return;
    }

    try {
      // First, lookup customer
      const response = await lookupAPI.customer(currentStore.id, qrToken.trim());
      setCustomerData(response.data);
      setManualQR('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código QR inválido o expirado');
      setCustomerData(null);
    }
  };

  const handleRegisterVisit = async () => {
    if (!customerData) return;
    setProcessingVisit(true);

    try {
      await visitsAPI.register(currentStore.id, manualQR || customerData.customer.id + ':' + Date.now() + ':temp');
      toast.success('¡Visita registrada!', {
        description: `${customerData.customer.full_name} ganó puntos`
      });
      // Play success sound
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2telehoAQJ/telehoAABAAAA');
      audio.play().catch(() => {});
      
      loadTodayVisits();
      setCustomerData(null);
      setScannerOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar visita');
    } finally {
      setProcessingVisit(false);
    }
  };

  const handleSelectReward = (reward) => {
    setSelectedReward(reward);
    setRedeemDialogOpen(true);
  };

  const handleRedeemReward = async () => {
    if (!selectedReward || !customerData) return;
    setProcessingRedeem(true);

    try {
      await redemptionsAPI.create(currentStore.id, customerData.customer.id, selectedReward.id);
      toast.success('¡Premio canjeado!', {
        description: `${selectedReward.name} entregado a ${customerData.customer.full_name}`
      });
      
      // Refresh customer data
      const response = await lookupAPI.customer(currentStore.id, customerData.customer.id + ':' + Date.now() + ':temp');
      setCustomerData(response.data);
      setRedeemDialogOpen(false);
      setSelectedReward(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al canjear premio');
    } finally {
      setProcessingRedeem(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center">
            <Store className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h1 className="font-outfit text-xl font-bold text-foreground">{currentStore.name}</h1>
            <p className="text-sm text-muted-foreground">{user?.full_name} - Staff</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={clearStore}
            className="text-muted-foreground"
            data-testid="change-store-btn"
          >
            <Store className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-muted-foreground"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <div className="p-6 max-w-6xl mx-auto">
        {/* Main Action - Scan QR */}
        <div className="mb-8">
          <Button
            onClick={() => setScannerOpen(true)}
            className="w-full h-32 text-2xl font-outfit bg-indigo-600 hover:bg-indigo-500 rounded-2xl tablet-button"
            data-testid="scan-qr-main-btn"
          >
            <Scan className="w-10 h-10 mr-4" />
            Escanear QR de Cliente
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="glass border-white/10">
            <CardContent className="p-6 text-center">
              <p className="font-outfit text-4xl font-bold text-foreground">{todayVisits.length}</p>
              <p className="text-sm text-muted-foreground mt-1">Visitas Hoy</p>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="p-6 text-center">
              <p className="font-outfit text-4xl font-bold text-foreground">
                {todayVisits.reduce((sum, v) => sum + v.points_earned, 0)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Puntos Otorgados</p>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="p-6 text-center">
              <p className="font-outfit text-4xl font-bold text-indigo-400">
                <Clock className="w-8 h-8 mx-auto" />
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Today's Visits */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2 text-foreground">
              <History className="w-5 h-5" />
              Visitas de Hoy
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayVisits.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No hay visitas registradas hoy</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {todayVisits.map((visit) => (
                  <div
                    key={visit.id}
                    className="flex items-center gap-4 p-3 rounded-xl bg-white/5"
                    data-testid={`visit-item-${visit.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-green-600/20 flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">Visita registrada</p>
                      <p className="text-xs text-muted-foreground">{formatRelativeTime(visit.created_at)}</p>
                    </div>
                    <Badge className="bg-green-600/20 text-green-400 border-0">
                      +{visit.points_earned} pts
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Scanner Dialog */}
      <Dialog open={scannerOpen} onOpenChange={setScannerOpen}>
        <DialogContent className="glass-panel border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl text-foreground">
              {customerData ? 'Cliente Identificado' : 'Escanear QR'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {customerData ? 'Verifica los datos y registra la visita' : 'Ingresa el código QR del cliente'}
            </DialogDescription>
          </DialogHeader>

          {!customerData ? (
            <div className="space-y-6 py-4">
              {/* Manual QR Input */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Código QR</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Pega o escribe el código QR aquí..."
                    value={manualQR}
                    onChange={(e) => setManualQR(e.target.value)}
                    className="bg-white/5 border-white/10 text-foreground"
                    data-testid="qr-input"
                  />
                  <Button
                    onClick={() => handleScanQR(manualQR)}
                    className="bg-indigo-600 hover:bg-indigo-500"
                    data-testid="scan-qr-btn"
                  >
                    <Scan className="w-4 h-4 mr-2" />
                    Buscar
                  </Button>
                </div>
              </div>

              {/* Scanner Frame Placeholder */}
              <div className="relative h-64 rounded-2xl bg-black/50 flex items-center justify-center">
                <div className="scanner-frame" />
                <div className="text-center z-10">
                  <QrCode className="w-16 h-16 mx-auto mb-4 text-white/30" />
                  <p className="text-white/50">Cámara no disponible en demo</p>
                  <p className="text-xs text-white/30 mt-1">Usa el campo de texto para ingresar el QR</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Customer Info */}
              <div className="glass rounded-xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-indigo-600/20 flex items-center justify-center text-2xl font-outfit font-bold text-indigo-400">
                    {getInitials(customerData.customer.full_name)}
                  </div>
                  <div>
                    <h3 className="font-outfit text-xl font-bold text-foreground">{customerData.customer.full_name}</h3>
                    <p className="text-muted-foreground">{customerData.customer.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 rounded-lg bg-white/5">
                    <p className="font-outfit text-2xl font-bold text-foreground">
                      {customerData.membership?.total_points || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Puntos</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-white/5">
                    <p className="font-outfit text-2xl font-bold text-foreground">
                      {customerData.membership?.total_visits || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Visitas</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-amber-600/20">
                    <Trophy className="w-6 h-6 mx-auto text-amber-400" />
                    <p className="text-xs text-amber-400 mt-1">{customerData.current_level?.name || 'Nuevo'}</p>
                  </div>
                </div>
              </div>

              {/* Register Visit Button */}
              <Button
                onClick={handleRegisterVisit}
                disabled={processingVisit}
                className="w-full h-16 text-xl font-outfit bg-green-600 hover:bg-green-500"
                data-testid="register-visit-btn"
              >
                {processingVisit ? (
                  <>
                    <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                    Registrando...
                  </>
                ) : (
                  <>
                    <Check className="w-6 h-6 mr-2" />
                    Registrar Visita
                  </>
                )}
              </Button>

              {/* Available Rewards */}
              {customerData.available_rewards?.length > 0 && (
                <div>
                  <h4 className="font-outfit font-semibold mb-3 text-foreground">Premios Disponibles para Canjear</h4>
                  <div className="space-y-2">
                    {customerData.available_rewards.map((reward) => (
                      <Card
                        key={reward.id}
                        className="glass border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => handleSelectReward(reward)}
                        data-testid={`select-reward-${reward.id}`}
                      >
                        <CardContent className="p-4 flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-amber-600/20 flex items-center justify-center">
                            <Gift className="w-6 h-6 text-amber-400" />
                          </div>
                          <div className="flex-1">
                            <h5 className="font-medium text-foreground">{reward.name}</h5>
                            <p className="text-sm text-muted-foreground">{formatPoints(reward.points_cost)}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <Button
                variant="outline"
                onClick={() => {
                  setCustomerData(null);
                  setScannerOpen(false);
                }}
                className="w-full border-white/10 text-foreground"
                data-testid="close-scanner-btn"
              >
                Cerrar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Redeem Confirmation Dialog */}
      <Dialog open={redeemDialogOpen} onOpenChange={setRedeemDialogOpen}>
        <DialogContent className="glass-panel border-white/10">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-foreground">Confirmar Canje</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Estás seguro de canjear este premio?
            </DialogDescription>
          </DialogHeader>

          {selectedReward && (
            <div className="py-4">
              <Card className="glass border-white/10">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-amber-600/20 flex items-center justify-center">
                    <Gift className="w-7 h-7 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-outfit font-semibold text-foreground">{selectedReward.name}</h4>
                    <p className="text-muted-foreground">{formatPoints(selectedReward.points_cost)}</p>
                  </div>
                </CardContent>
              </Card>

              <div className="mt-4 p-4 rounded-xl bg-amber-600/10 border border-amber-600/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-300">
                    Se descontarán {formatPoints(selectedReward.points_cost)} del cliente. 
                    Esta acción no se puede deshacer.
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRedeemDialogOpen(false)}
              className="border-white/10 text-foreground"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRedeemReward}
              disabled={processingRedeem}
              className="bg-amber-600 hover:bg-amber-500"
              data-testid="confirm-redeem-btn"
            >
              {processingRedeem ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Canjeando...
                </>
              ) : (
                <>
                  <Gift className="w-4 h-4 mr-2" />
                  Confirmar Canje
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Store App Router
export default function StoreApp() {
  const { currentStore, selectStore } = useStore();

  if (!currentStore) {
    return <StoreAppSelector onSelect={selectStore} />;
  }

  return (
    <Routes>
      <Route path="/" element={<StoreAppDashboard />} />
      <Route path="*" element={<StoreAppDashboard />} />
    </Routes>
  );
}
